/**
 * VALET palette + typography — pulled from the marketing site so the
 * dashboard reads as part of the same product family.
 *
 * Marketing site reference: green #123524 + bronze #8b6f47 over a clean
 * white background with Inter as the workhorse and Fraunces for editorial
 * display headings.
 */

export const VALET = {
    // Surfaces
    bg: '#ffffff',
    bgMuted: '#f7f5f0',           // warm off-white, used for the page chrome
    surface: '#ffffff',
    surfaceMuted: '#fafaf7',
    surfaceHover: '#f1efe9',

    // Primary brand
    green: '#123524',
    greenDark: '#0d281c',
    greenLight: '#eef2f0',
    greenHairline: 'rgba(18, 53, 36, 0.12)',

    // Bronze accent
    bronze: '#8b6f47',
    bronzeLight: '#b89569',
    bronzeMuted: '#f6f1e8',
    bronzeHairline: 'rgba(139, 111, 71, 0.22)',

    // Ink / type
    ink: '#0a0a0a',
    textPrimary: '#0a0a0a',
    textSecondary: '#4a4a48',
    textMuted: '#8b8a85',
    textDim: '#b8b6b0',

    // Semantic
    success: '#1f7a4c',
    successBg: '#e8f4ed',
    danger: '#a03028',
    dangerBg: '#fbeceb',
    warning: '#a86a1e',
    warningBg: '#f9efdd',
    info: '#2c5d8c',
    infoBg: '#e6eef6',

    // Generic borders & shadows
    border: 'rgba(10, 10, 10, 0.08)',
    borderStrong: 'rgba(10, 10, 10, 0.16)',
    hairline: 'rgba(139, 111, 71, 0.18)',
    shadow: '0 1px 2px rgba(10, 10, 10, 0.04), 0 4px 12px rgba(10, 10, 10, 0.04)',
    shadowHover: '0 2px 4px rgba(10, 10, 10, 0.06), 0 8px 24px rgba(10, 10, 10, 0.06)',
};

export const FONTS = {
    sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    display: '"Fraunces", "Times New Roman", Georgia, serif',
    mono: '"SF Mono", "JetBrains Mono", "Fira Code", monospace',
};

export const TYPE = {
    // Editorial display — section heads, marquee numbers
    display: {
        fontFamily: FONTS.display,
        fontWeight: 500,
        letterSpacing: '-0.025em',
    },
    // Big numerics in KPI cards
    kpiValue: {
        fontFamily: FONTS.display,
        fontWeight: 500,
        fontSize: '34px',
        lineHeight: 1.05,
        letterSpacing: '-0.03em',
        color: VALET.ink,
    },
    // KPI label
    kpiLabel: {
        fontFamily: FONTS.mono,
        fontSize: '10px',
        fontWeight: 500,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: VALET.textMuted,
    },
    // Section headings (Fraunces, larger)
    sectionTitle: {
        fontFamily: FONTS.display,
        fontWeight: 500,
        fontSize: '22px',
        letterSpacing: '-0.02em',
        color: VALET.ink,
    },
    // Eyebrow / kicker (mono uppercase) — used SPARINGLY (Rishi's
    // marketing-site preference is to mostly avoid them, but they're OK on
    // an admin dashboard where mono signals data/metric context).
    eyebrow: {
        fontFamily: FONTS.mono,
        fontSize: '10px',
        fontWeight: 500,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: VALET.bronze,
    },
    // Body
    body: {
        fontFamily: FONTS.sans,
        fontSize: '14px',
        lineHeight: 1.5,
        color: VALET.textPrimary,
    },
    bodySmall: {
        fontFamily: FONTS.sans,
        fontSize: '12px',
        lineHeight: 1.5,
        color: VALET.textSecondary,
    },
};
